import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LifeBuoy, ShieldAlert, Check, MapPin } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  HelpRequest,
  RequestKind,
  responderKinds,
  resolveHelpRequest,
  subscribeOpenHelpRequests,
} from '../../services/helpRequestService';
import { soundNotificationService, SoundType } from '../../services/soundNotificationService';

function prettyPage(path: string): string {
  if (!path || path === '/') return 'Dashboard';
  const first = path.split('/').filter(Boolean)[0] || '';
  if (!first) return 'Dashboard';
  return first
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function timeAgo(ms: number | null): string {
  if (!ms) return 'just now';
  const secs = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  return `${mins} min${mins === 1 ? '' : 's'} ago`;
}

function soundsForKinds(kinds: Set<RequestKind>): SoundType[] {
  // Security is the most urgent — use a distinct, harsher tone.
  if (kinds.has('security')) return [SoundType.Error, SoundType.Error, SoundType.Error];
  return [SoundType.Bell, SoundType.Bell, SoundType.Bell];
}

/**
 * Mounted globally in Layout. For designated responders only (Help → Zach/Ty,
 * Security → Jason) it listens for open requests of the kinds they handle and
 * surfaces a prominent popup + repeating ding until acknowledged. Renders
 * nothing for everyone else.
 */
export const HelpRequestListener: React.FC = () => {
  const { currentUser } = useAuth();
  const myKinds = responderKinds(currentUser?.email);
  const isResponder = myKinds.length > 0;
  const myKindSet = new Set<RequestKind>(myKinds);

  const [requests, setRequests] = useState<HelpRequest[]>([]);
  const seenIdsRef = useRef<Set<string>>(new Set());
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!isResponder || !currentUser) return;

    try {
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
    } catch {
      /* ignore */
    }

    const unsub = subscribeOpenHelpRequests((rows) => {
      // Only the kinds this responder handles.
      const mine = rows.filter((r) => myKindSet.has(r.kind));

      const isFirst = !initializedRef.current;
      const fresh = mine.filter((r) => !seenIdsRef.current.has(r.id));
      mine.forEach((r) => seenIdsRef.current.add(r.id));

      if (fresh.length > 0 && !isFirst) {
        const freshKinds = new Set<RequestKind>(fresh.map((r) => r.kind));
        soundNotificationService.playSequence(soundsForKinds(freshKinds), 400).catch(() => {});

        try {
          if ('Notification' in window && Notification.permission === 'granted') {
            const r = fresh[0];
            const label = r.kind === 'security' ? 'SECURITY requested' : 'Help requested';
            new Notification(label, {
              body: `${r.requesterName}${r.page ? ` · ${prettyPage(r.page)}` : ''}`,
              tag: `req-${r.id}`,
            });
          }
        } catch {
          /* ignore */
        }
      }
      initializedRef.current = true;

      setRequests(mine);
    });

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResponder, currentUser]);

  // Keep dinging every 30s while at least one request is still open, until the
  // responder taps "I've got it" on all of them.
  useEffect(() => {
    if (!isResponder || requests.length === 0) return;
    const kinds = new Set<RequestKind>(requests.map((r) => r.kind));
    const id = setInterval(() => {
      soundNotificationService.playSequence(soundsForKinds(kinds), 400).catch(() => {});
    }, 30000);
    return () => clearInterval(id);
  }, [isResponder, requests]);

  const handleResolve = async (req: HelpRequest) => {
    setRequests((cur) => cur.filter((r) => r.id !== req.id));
    try {
      await resolveHelpRequest(
        req.id,
        currentUser?.uid || '',
        currentUser?.displayName || currentUser?.email || '',
      );
    } catch (err) {
      console.error('Failed to resolve request', err);
    }
  };

  if (!isResponder) return null;

  return (
    <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[2147483000] flex flex-col items-center gap-3 w-full max-w-md px-3 pointer-events-none">
      <AnimatePresence>
        {requests.map((req) => {
          const security = req.kind === 'security';
          const headerBg = security
            ? 'bg-gradient-to-r from-rose-600 to-red-600'
            : 'bg-gradient-to-r from-emerald-600 to-green-600';
          const ring = security ? 'ring-rose-500/70' : 'ring-emerald-500/70';
          const Icon = security ? ShieldAlert : LifeBuoy;
          const title = security
            ? `${req.requesterName} needs SECURITY`
            : `${req.requesterName} needs help`;
          return (
            <motion.div
              key={req.id}
              initial={{ opacity: 0, y: -28, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.94 }}
              transition={{ type: 'spring', damping: 20, stiffness: 320 }}
              className={`pointer-events-auto w-full overflow-hidden rounded-2xl bg-white shadow-[0_20px_60px_-12px_rgba(0,0,0,0.45)] ring-2 ${ring}`}
            >
              <div className={`flex items-center gap-3 px-5 py-4 ${headerBg}`}>
                <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/20">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/40" />
                  <Icon className="relative h-6 w-6 text-white" />
                </span>
                <div className="min-w-0">
                  <p className="text-[16px] font-bold text-white leading-tight">{title}</p>
                  <p className="flex items-center gap-1 text-[12px] text-white/85">
                    <MapPin className="h-3.5 w-3.5" />
                    {prettyPage(req.page)}
                    <span className="mx-1">·</span>
                    {timeAgo(req.createdAt?.toMillis?.() ?? null)}
                  </p>
                </div>
              </div>
              <div className="px-4 py-3">
                <button
                  type="button"
                  onClick={() => handleResolve(req)}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-gray-900 px-3 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-gray-800"
                >
                  <Check className="h-4 w-4" />
                  I've got it
                </button>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

export default HelpRequestListener;
